import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Switch } from '@/components/ui/switch';
import { useCookieConsent } from '@/contexts/CookieConsentContext';
import { BarChart3, Cookie, Heart, Settings, Shield, Target } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

export const ConsentBanner: React.FC = () => {
  const { t } = useTranslation('cookies');
  const { showBanner, consent, acceptAll, rejectAll, updateConsent } = useCookieConsent();
  const [showSettings, setShowSettings] = useState(false);
  const [tempConsent, setTempConsent] = useState(consent);

  // Listen for footer cookie settings event
  useEffect(() => {
    const handleShowCookieSettings = () => {
      setShowSettings(true);
    };

    window.addEventListener('showCookieSettings', handleShowCookieSettings);

    return () => {
      window.removeEventListener('showCookieSettings', handleShowCookieSettings);
    };
  }, []);

  if (!showBanner && !showSettings) {return null;}

  const handleSaveSettings = () => {
    updateConsent(tempConsent);
    setShowSettings(false);
  };

  const cookieCategories = [
    {
      key: 'essential',
      icon: Shield,
      required: true,
    },
    {
      key: 'analytics',
      icon: BarChart3,
      required: false,
    },
    {
      key: 'marketing',
      icon: Target,
      required: false,
    },
    {
      key: 'comfort',
      icon: Heart,
      required: false,
    }
  ];

  return (
    <>
      {/* Settings Sheet - Always available */}
      <Sheet open={showSettings} onOpenChange={setShowSettings}>
        <SheetContent className="w-full sm:max-w-lg overflow-y-auto">
          <SheetHeader>
            <SheetTitle className="flex items-center gap-2">
              <Cookie className="h-5 w-5" />
              {t('settings.title')}
            </SheetTitle>
          </SheetHeader>

          <div className="mt-6 space-y-4">
            <p className="text-sm text-muted-foreground">
              {t('settings.description')}
            </p>

            {cookieCategories.map((category) => {
              const Icon = category.icon;
              const isEnabled = tempConsent[category.key as keyof typeof tempConsent];

              return (
                <Card key={category.key} className="border">
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <Icon className="h-5 w-5 text-accent-primary" />
                          <h4 className="font-medium text-foreground">
                            {t(`categories.${category.key}.title`)}
                            {category.required && (
                              <span className="ml-2 text-xs bg-accent-primary text-primary-foreground px-2 py-0.5 rounded">
                                {t('settings.required')}
                              </span>
                            )}
                          </h4>
                        </div>
                        <p className="text-sm text-muted-foreground mb-2">
                          {t(`categories.${category.key}.description`)}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {t(`categories.${category.key}.details`)}
                        </p>
                      </div>
                      <div className="flex-shrink-0">
                        <Switch
                          checked={Boolean(isEnabled)}
                          disabled={category.required}
                          onCheckedChange={(checked) => {
                            if (!category.required) {
                              setTempConsent(prev => ({
                                ...prev,
                                [category.key]: checked
                              }));
                            }
                          }}
                        />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}

            <div className="flex flex-col gap-fluid-xs pt-4 border-t">
              <Button onClick={handleSaveSettings} className="w-full">
                {t('settings.save')}
              </Button>
              <div className="grid grid-cols-2 gap-fluid-xs">
                <Button
                  variant="outline"
                  onClick={() => {
                    setTempConsent({
                      essential: true,
                      analytics: true,
                      marketing: true,
                      comfort: true
                    });
                  }}
                  className="w-full"
                >
                  {t('settings.acceptAll')}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => {
                    setTempConsent({
                      essential: true,
                      analytics: false,
                      marketing: false,
                      comfort: false
                    });
                  }}
                  className="w-full"
                >
                  {t('settings.essentialOnly')}
                </Button>
              </div>
            </div>
          </div>
        </SheetContent>
      </Sheet>

      {/* Cookie Banner - Only when showBanner is true */}
      {showBanner && (
        <div className="fixed bottom-0 left-0 right-0 z-50 p-4 bg-background/95 backdrop-blur-sm border-t shadow-card">
          <Card className="mx-auto max-w-6xl">
            <CardContent className="p-6">
              <div className="flex flex-col md:flex-row items-start md:items-center gap-fluid-sm">
                <div className="flex items-center gap-fluid-sm flex-1">
                  <Cookie className="h-8 w-8 text-accent-primary flex-shrink-0" />
                  <div className="flex-1">
                    <h3 className="font-semibold text-foreground mb-2">
                      {t('banner.title')}
                    </h3>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {t('banner.description')}{' '}
                      <Link to="/datenschutz" className="text-accent-primary hover:text-primary underline">
                        {t('banner.privacyPolicy')}
                      </Link>.
                    </p>
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row gap-fluid-xs w-full md:w-auto">
                  <Button
                    variant="outline"
                    className="w-full sm:w-auto"
                    onClick={() => setShowSettings(true)}
                  >
                    <Settings className="h-4 w-4 mr-2" />
                    {t('banner.settings')}
                  </Button>

                  <Button variant="outline" onClick={rejectAll} className="w-full sm:w-auto">
                    {t('banner.reject')}
                  </Button>
                  <Button onClick={acceptAll} className="w-full sm:w-auto">
                    {t('banner.acceptAll')}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </>
  );
};
